<script setup lang="ts">
/* Composables */
import { useProductModal } from '@/composables/useProductModal'

/* Stores */
import { useToggleStore } from '@/store/toggle/toggle'

/* Types */
import { CategoryPage } from '@/types/api/api-v3/category'
import { Product } from '@/types/api/api-v3/product'
import { Factoid } from '@/types/api/api-v3/slider'
import { NAME_PARENT_COMPONENT } from '@/types/products'

/* Components */
import MainCategoryFactoid from '@/components/Main/MainCategoryFactoid.vue'
import ProductCard from '@/components/Product/ProductCard/ProductCard.vue'
import ProductItem from '@/components/Product/ProductItem/ProductItem.vue'

type Props = {
  currentCategory: CategoryPage
  filteredProducts: Product[]
  templateFactoid?: Factoid
  isLoading?: boolean
}

const props = withDefaults(defineProps<Props>(), {
  filteredProducts: () => [],
  templateFactoid: null,
})

const route = useRoute()

const { isDesktop, isTablet, isMobile } = useDevice()

const { newProductCardEnabled } = useToggleStore()

const factoidLength = computed(() => {
  // Вычисляем число активных фактоидов(1 фактоид из списка
  // на три продукта на странице
  // также проверка на то чтобы всегда остовался 1 фактоид если фактоидов
  // в изначальном списке больше 0
  const productsLength = props.filteredProducts.length
  let factoidsLength = props.currentCategory.factoids.length

  if (factoidsLength !== 0) {
    while (productsLength / factoidsLength < 3) {
      factoidsLength--
    }
  }

  if (factoidsLength === 0 && props.currentCategory.factoids.length !== 0) {
    factoidsLength = 1
  }

  if (productsLength === 0) {
    factoidsLength = 0
  }

  return factoidsLength
})

const factoidStyles = computed(() => {
  const styles: string[] = []
  let row = 0
  let column = 4
  let gridStyle

  // Стили для активных фактоидов
  for (let i = 1; i <= factoidLength.value; i++) {
    if (isDesktop || i === 1) {
      row++
    } else if (isTablet) {
      row += 2
    }

    if (i % 2 === 0) {
      isDesktop ? (column = 4) : (column = 2)

      if (isMobile) {
        row += 2
      }
    } else {
      column = 1

      if (isMobile && i !== 1) {
        row += 3
      }

      if (isTablet && i !== 1) {
        column = 2
      }
    }

    gridStyle = 'grid-row:' + row + '; grid-column:' + column + ';'
    styles.push(gridStyle)
  }
  return styles
})

const factoidMap = computed(() => {
  if (route.name === 'filter-category' || !props.currentCategory?.factoids) {
    return []
  }

  return props.currentCategory.factoids.slice(0, factoidLength.value) || []
})

const { productDialog, openDialogProduct, closeDialogProduct } =
  useProductModal()

const onOpenDialogProduct = (product: Product) => {
  openDialogProduct({
    currentProduct: product,
    productList: props.currentCategory.products,
    visible: true,
  })
}

// TODO: Удалить после того как все города перейдут на новый дизайн карточек
const factiodOldClass = computed(() => {
  return newProductCardEnabled ? '' : 'category-products__factoid--old'
})
</script>

<template>
  <div class="category-products">
    <div
      v-if="isDesktop"
      class="category-products__list"
      :class="{ 'category-products__list--old': !newProductCardEnabled }"
    >
      <template v-if="newProductCardEnabled">
        <product-card
          v-for="product in filteredProducts"
          :key="product.id"
          :product="product"
          :is-loading="isLoading"
          @on-open-dialog="onOpenDialogProduct"
        />
      </template>

      <template v-else>
        <product-item
          v-for="product in filteredProducts"
          :key="product.id"
          :product="product"
          :parent-component="NAME_PARENT_COMPONENT.MAIN_DESKTOP"
          @open-dialog-product="onOpenDialogProduct"
        />
      </template>

      <div
        v-if="templateFactoid"
        class="category-products__factoid"
        :class="factiodOldClass"
        :style="factoidStyles[0]"
      >
        <main-category-factoid :factoid="templateFactoid" is-category-page />
      </div>

      <template v-else>
        <div
          v-for="(factoid, index) in factoidMap"
          :key="factoid.url"
          class="category-products__factoid"
          :class="factiodOldClass"
          :style="factoidStyles[index]"
        >
          <main-category-factoid :factoid="factoid" is-category-page />
        </div>
      </template>
    </div>

    <div v-else class="category-products__list">
      <template v-if="newProductCardEnabled">
        <product-card
          v-for="product in filteredProducts"
          :key="product.id"
          :product="product"
          :is-loading="isLoading"
          @on-open-dialog="onOpenDialogProduct"
        />
      </template>

      <template v-else>
        <product-item
          v-for="product in filteredProducts"
          :key="product.id"
          :product="product"
          :parent-component="NAME_PARENT_COMPONENT.MAIN_MOBILE"
          @open-dialog-product="onOpenDialogProduct"
        />
      </template>

      <div
        v-for="(factoid, index) in factoidMap"
        :key="factoid.url"
        class="category-products__factoid"
        :class="factiodOldClass"
        :style="factoidStyles[index]"
      >
        <main-category-factoid :factoid="factoid" is-category-page />
      </div>
    </div>

    <product-modal
      :product-dialog="productDialog"
      @close-dialog-product="closeDialogProduct"
    />
  </div>
</template>

<style lang="scss" scoped>
.category-products {
  &__list {
    display: grid;
    grid-template-columns: repeat(auto-fit, 276px);
    gap: 100px 32px;

    // TODO: Удалить после того как все города перейдут на новый дизайн карточек
    &--old {
      grid-template-columns: repeat(auto-fit, 270px);
      gap: 100px 36px;
    }

    @include media('<=portrait-phone', '>=phone') {
      grid-template-columns: repeat(2, minmax(145px, 1fr));
      gap: 10px;
      padding: 25px 0 0;
    }

    @include media('<=tablet', '>=portrait-phone') {
      grid-template-columns: repeat(2, minmax(120px, 225px));
      gap: 10px;
      padding: 25px 0 0;
    }

    // Границы ширины [от 768 до 1024]
    @include media('>=tablet', '<=desktop-mini') {
      grid-template-columns: repeat(3, minmax(217px, 1fr));
      gap: 10px;
      padding: 25px 0 0;
    }
  }

  &__factoid {
    grid-row: 1;
    height: 100%;
    min-height: 392px;

    &--old {
      :deep(.factoid) {
        width: 100%;
        height: 90%;
        min-height: 240px;

        @include media('>=tablet') {
          width: 270px;
          height: 100%;
          max-height: 410px;
        }
      }
    }

    @include media('<=desktop-mini') {
      min-height: auto;
    }
  }
}
</style>
